import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Award, Check, CreditCard, FileText, Leaf, ShieldCheck, Sparkles, Upload, X, Zap } from "lucide-react";
import api, { formatApiError, fileToBase64 } from "@/lib/api";
import { bestPromo, fetchActivePromos, promoLabel, validatePromoCode } from "@/lib/promo";
import { PageHero, Reveal, PhotoSlot, EmptyState, UniverseSection, FaqSection, siteText, useSettings } from "@/components/shared";
import CustomSections from "@/components/layout/CustomSections";

const fmt = (n) => n.toFixed(2).replace(".", ",") + " €";

const SERVICE_CATEGORIES = [
  { id: "bureaux-commerces", label: "Bureaux & commerces" },
  { id: "maisons-appartements", label: "Maisons & appartements" },
  { id: "remise-en-etat", label: "Remise en état" },
  { id: "vitrines-communs", label: "Vitrines & parties communes" },
];
const categoryLabel = (id) => SERVICE_CATEGORIES.find((c) => c.id === id)?.label || "";

const emptyQuote = { name: "", email: "", phone: "", service: "", address: "", surface: "", preferred_date: "", photos: [], message: "" };

export default function Services() {
  const settings = useSettings();
  const [services, setServices] = useState(null);
  const [quote, setQuote] = useState(null);
  const [booking, setBooking] = useState(null);
  const [sending, setSending] = useState(false);
  const [quoteSent, setQuoteSent] = useState(false);
  const [autoPromos, setAutoPromos] = useState([]);
  const [application, setApplication] = useState({ name: "", email: "", phone: "", message: "" });
  const [cvFile, setCvFile] = useState(null);
  const [appSending, setAppSending] = useState(false);
  const [appSent, setAppSent] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [detail, setDetail] = useState(null);
  const [activeCategory, setActiveCategory] = useState("");
  const location = useLocation();

  const heroCfg = settings?.page_heroes?.services || {};

  useEffect(() => {
    api
      .get("/services")
      .then((r) => setServices(r.data))
      .catch(() => setServices([]));
    fetchActivePromos("services").then(setAutoPromos);
    api
      .get("/gallery")
      .then((r) => setGallery(r.data.filter((g) => g.scope === "services")))
      .catch(() => {});
  }, []);

  // Lien direct « Demander un devis » (ex : bouton d'en-tête) : /services#devis
  useEffect(() => {
    if (location.hash === "#devis") {
      setQuote({ ...emptyQuote });
    }
  }, [location.hash]);

  const visibleServices = (services || []).filter(
    (s) => !activeCategory || s.category === activeCategory
  );
  const usedCategories = SERVICE_CATEGORIES.filter((c) =>
    (services || []).some((s) => s.category === c.id)
  );

  const addQuotePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const current = quote?.photos || [];
    const room = Math.max(0, 4 - current.length);
    if (room === 0) {
      toast.error("4 photos maximum.");
      return;
    }
    const encoded = [];
    for (const f of files.slice(0, room)) {
      encoded.push(await fileToBase64(f));
    }
    setQuote((q) => ({ ...q, photos: [...(q.photos || []), ...encoded] }));
    e.target.value = "";
  };

  const galleryPhotos = gallery.filter((g) => !g.variant);
  const avantApres = Object.values(
    gallery.reduce((acc, g) => {
      if (g.variant && g.pair_key) {
        acc[g.pair_key] = { ...acc[g.pair_key], [g.variant]: g };
      }
      return acc;
    }, {})
  ).filter((pair) => pair.avant && pair.apres);

  const submitApplication = async (e) => {
    e.preventDefault();
    if (!application.name || !application.email) {
      toast.error("Merci d'indiquer votre nom et votre email.");
      return;
    }
    if (!cvFile) {
      toast.error("Merci de joindre votre CV (PDF, DOC ou DOCX, 5 Mo max).");
      return;
    }
    setAppSending(true);
    try {
      const fd = new FormData();
      fd.append("name", application.name);
      fd.append("email", application.email);
      fd.append("phone", application.phone);
      fd.append("message", application.message);
      fd.append("file", cvFile);
      await api.post("/services/applications", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAppSent(true);
      setApplication({ name: "", email: "", phone: "", message: "" });
      setCvFile(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAppSending(false);
    }
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/services/quotes", quote);
      setQuoteSent(true);
      setQuote(null);
      toast.success("Votre demande de devis a bien été envoyée.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!booking.name || !booking.email || !booking.date || !booking.time) {
      toast.error("Merci de compléter tous les champs obligatoires.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/services/${booking.service.id}/book`, {
        name: booking.name,
        email: booking.email,
        phone: booking.phone || "",
        date: booking.date,
        time: booking.time,
        address: booking.address || "",
        notes: booking.notes || "",
        quantity: parseInt(booking.quantity, 10),
        promo_code: booking.codePromo ? (booking.codeInput || "").trim().toUpperCase() : "",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSending(false);
    }
  };

  const applyBookingCode = async () => {
    if (!booking?.codeInput?.trim()) return;
    try {
      const p = await validatePromoCode(booking.codeInput.trim(), "services");
      setBooking((b) => ({ ...b, codePromo: p }));
      toast.success(`Code « ${p.name} » appliqué.`);
    } catch {
      setBooking((b) => ({ ...b, codePromo: null }));
      toast.error("Code promotionnel invalide ou expiré.");
    }
  };

  const bookingTotal = booking ? booking.service.price * (parseInt(booking.quantity, 10) || 1) : 0;
  const { promo: bookingPromo, discount: bookingDiscount } = booking
    ? bestPromo(autoPromos, booking.codePromo, bookingTotal)
    : { promo: null, discount: 0 };

  return (
    <div data-testid="services-page">
      <PageHero
        overline="Maasaï Services"
        title={heroCfg.title || "Ménage, nettoyage & entretien"}
        testId="services-hero"
        image={heroCfg.image || ""}
        ctas={[
          { label: "Nos prestations", href: "#prestations" },
          { label: "Demander un devis", onClick: () => setQuote({ ...emptyQuote }) },
        ]}
      >
        <p>
          {heroCfg.description || siteText(settings, "services_intro") ||
            "Des prestations professionnelles pour les particuliers et les professionnels : réservez en ligne les prestations tarifées, ou demandez un devis gratuit pour un besoin sur mesure."}
        </p>
      </PageHero>

      {/* Pourquoi nous choisir */}
      <section className="border-t border-white/5 px-6 py-20 md:px-12" data-testid="services-why-section">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <p className="overline mb-6">Pourquoi nous choisir</p>
            <h2 className="max-w-2xl text-2xl font-bold tracking-tight text-ivory sm:text-3xl">
              Une équipe professionnelle, un résultat impeccable
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: ShieldCheck, title: "Équipe de confiance", text: "Des professionnels formés et encadrés, pour un service fiable et soigné à chaque intervention." },
              { icon: Leaf, title: "Produits respectueux", text: "Des produits d'entretien adaptés et respectueux de vos espaces et de l'environnement." },
              { icon: Zap, title: "Intervention rapide", text: "Une organisation réactive pour répondre vite à vos besoins, même urgents." },
              { icon: Award, title: "Qualité garantie", text: "Le même niveau d'exigence que le Palais Maasaï, appliqué à chacune de nos prestations." },
            ].map((item, i) => (
              <Reveal key={item.title} delay={i * 0.08}>
                <div className="h-full rounded-lg border border-white/10 bg-coal/60 p-8 transition-colors hover:border-gold/40" data-testid={`services-why-${i}`}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-lg border border-gold/30 text-gold">
                    <item.icon className="h-5 w-5" strokeWidth={1.25} />
                  </span>
                  <h3 className="mt-6 text-base font-semibold tracking-tight text-ivory">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ivory/50">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="border-t border-white/5 px-6 py-20 md:px-12" data-testid="services-steps-section">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <p className="overline mb-6">Comment ça marche</p>
            <h2 className="max-w-2xl text-2xl font-bold tracking-tight text-ivory sm:text-3xl">
              Trois étapes, un espace impeccable
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              { n: "01", title: "Choisissez votre prestation", text: "Réservez en ligne une prestation tarifée, ou demandez un devis gratuit pour un besoin sur mesure." },
              { n: "02", title: "Nous confirmons ensemble", text: "Nous validons la date, l'heure et l'adresse de l'intervention. Paiement sécurisé en ligne." },
              { n: "03", title: "Profitez du résultat", text: "Notre équipe intervient avec le matériel adapté. Vous retrouvez un espace propre et sain." },
            ].map((step, i) => (
              <Reveal key={step.n} delay={i * 0.1}>
                <div className="h-full rounded-lg border border-white/10 p-8" data-testid={`services-step-${i}`}>
                  <span className="text-4xl font-bold tracking-tight text-gold/40">{step.n}</span>
                  <h3 className="mt-4 text-base font-semibold tracking-tight text-ivory">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ivory/50">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="prestations" className="border-t border-white/5 px-6 pb-28 pt-20 md:px-12 md:pb-40" data-testid="services-list-section">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="overline mb-6">Nos prestations</p>
                <h2 className="max-w-2xl text-2xl font-bold tracking-tight text-ivory sm:text-3xl">
                  Des services adaptés à chaque espace
                </h2>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-ivory/50">
                  Tarifs indicatifs, ajustés après étude de votre besoin. Les prestations complexes
                  sont proposées uniquement sur devis.
                </p>
              </div>
              <Link to="/contact?activity=services" className="btn-outline !h-11 !px-6 !text-[11px]" data-testid="services-contact-button">
                Contactez-nous
              </Link>
            </div>
          </Reveal>

          {usedCategories.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2" data-testid="services-category-filter">
              <button
                onClick={() => setActiveCategory("")}
                data-testid="services-filter-all"
                className={`rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors ${
                  activeCategory === "" ? "border-copper bg-copper text-ivory" : "border-white/15 text-ivory/55 hover:border-gold/50 hover:text-gold"
                }`}
              >
                Toutes
              </button>
              {usedCategories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setActiveCategory(c.id)}
                  data-testid={`services-filter-${c.id}`}
                  className={`rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors ${
                    activeCategory === c.id ? "border-copper bg-copper text-ivory" : "border-white/15 text-ivory/55 hover:border-gold/50 hover:text-gold"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}

          {quoteSent && (
            <Reveal className="mt-10">
              <div className="rounded-lg border border-gold/30 bg-coal/60 p-6 text-center" data-testid="quote-success">
                <p className="text-sm text-ivory/70">
                  Merci ! Votre demande de devis a bien été envoyée. Nous revenons vers vous
                  rapidement.
                </p>
              </div>
            </Reveal>
          )}

          <div className="mt-10">
          {services === null ? null : visibleServices.length === 0 ? (
            <EmptyState text="Les prestations arrivent bientôt." />
          ) : (
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4" data-testid="services-grid">
              {visibleServices.map((service, i) => {
                const { promo: cardPromo } = bestPromo(autoPromos, null, service.price);
                return (
                <Reveal key={service.id} delay={(i % 3) * 0.08}>
                  <div className="flex h-full flex-col rounded-lg border border-white/10 bg-coal/60 p-5" data-el="services.service_card" data-testid={`service-${service.id}`}>
                    <PhotoSlot src={service.photo} alt={service.name} ratio="aspect-[3/2]" />
                    {service.category && categoryLabel(service.category) && (
                      <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold/70" data-testid={`service-category-${service.id}`}>
                        {categoryLabel(service.category)}
                      </p>
                    )}
                    <h2 className="mt-2 text-base font-semibold tracking-tight text-ivory">
                      {service.name}
                    </h2>
                    {service.description && (
                      <p className="mt-2 flex-1 text-[13px] leading-relaxed text-ivory/50">
                        {service.description}
                      </p>
                    )}
                    <p className="mt-4 text-sm font-semibold text-gold">
                      {service.price > 0 ? (
                        cardPromo ? (
                          <>
                            <span className="mr-2 text-xs font-normal text-ivory/35 line-through">
                              {fmt(service.price)}
                            </span>
                            {fmt(service.price - bestPromo(autoPromos, null, service.price).discount)}
                            {service.unit ? ` ${service.unit}` : ""}
                            <span className="ml-2 rounded border border-gold/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider" data-testid={`service-promo-badge-${service.id}`}>
                              {promoLabel(cardPromo)}
                            </span>
                          </>
                        ) : (
                          `${fmt(service.price)}${service.unit ? ` ${service.unit}` : ""} ${service.tax === "ht" ? "HT" : "TTC"}`
                        )
                      ) : (
                        "Sur devis"
                      )}
                    </p>
                    {service.price > 0 && (
                      <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-ivory/35" data-testid={`service-indicative-${service.id}`}>
                        Tarif indicatif
                      </p>
                    )}
                    <div className="mt-5 flex gap-3">
                      {service.price > 0 ? (
                        <button
                          onClick={() =>
                            setBooking({
                              service,
                              name: "",
                              email: "",
                              phone: "",
                              date: "",
                              time: "",
                              address: "",
                              notes: "",
                              quantity: "1",
                              codeInput: "",
                              codePromo: null,
                            })
                          }
                          className="btn-primary flex-1 !h-11 !text-[11px]"
                          data-el="services.book_button"
                          data-testid={`service-book-${service.id}`}
                        >
                          Réserver
                        </button>
                      ) : null}
                      <button
                        onClick={() => setQuote({ ...emptyQuote, service: service.name })}
                        className={`${service.price > 0 ? "btn-outline" : "btn-primary"} flex-1 !h-11 !text-[11px]`}
                        data-testid={`service-quote-${service.id}`}
                      >
                        <FileText className="h-4 w-4" strokeWidth={1.5} />
                        Devis
                      </button>
                    </div>
                    <div className={`mt-3 flex items-center ${service.details ? "justify-between" : "justify-end"}`}>
                      {service.details && (
                        <button
                          onClick={() => setDetail(service)}
                          className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ivory/45 transition-colors hover:text-gold"
                          data-testid={`service-details-${service.id}`}
                        >
                          En savoir plus
                        </button>
                      )}
                      <Link
                        to="/contact?activity=services"
                        className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ivory/45 transition-colors hover:text-gold"
                        data-testid={`service-contact-${service.id}`}
                      >
                        Contactez-nous
                      </Link>
                    </div>
                  </div>
                </Reveal>
                );
              })}
            </div>
          )}
          </div>
        </div>
      </section>

      {/* Modal détail prestation */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="service-detail-modal">
          <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg border border-white/10 bg-coal p-8">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-ivory">{detail.name}</h3>
              <button type="button" onClick={() => setDetail(null)} aria-label="Fermer" data-testid="service-detail-close">
                <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
              </button>
            </div>
            <div className="mt-5">
              <PhotoSlot src={detail.photo} alt={detail.name} ratio="aspect-[16/9]" />
            </div>
            {detail.description && (
              <p className="mt-5 text-sm leading-relaxed text-ivory/60">{detail.description}</p>
            )}
            <div className="mt-4 whitespace-pre-line text-sm leading-relaxed text-ivory/55" data-testid="service-detail-content">
              {detail.details}
            </div>
            <p className="mt-5 text-sm font-semibold text-gold">
              {detail.price > 0 ? `${fmt(detail.price)}${detail.unit ? ` ${detail.unit}` : ""}` : "Sur devis"}
            </p>
            <div className="mt-6 flex gap-3">
              {detail.price > 0 && (
                <button
                  onClick={() => {
                    setBooking({
                      service: detail,
                      name: "", email: "", phone: "", date: "", time: "",
                      address: "", notes: "", quantity: "1", codeInput: "", codePromo: null,
                    });
                    setDetail(null);
                  }}
                  className="btn-primary flex-1 !h-11 !text-[11px]"
                  data-testid="service-detail-book"
                >
                  Réserver
                </button>
              )}
              <button
                onClick={() => {
                  setQuote({ ...emptyQuote, service: detail.name });
                  setDetail(null);
                }}
                className={`${detail.price > 0 ? "btn-outline" : "btn-primary"} flex-1 !h-11 !text-[11px]`}
                data-testid="service-detail-quote"
              >
                Demander un devis
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal devis */}
      {quote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="quote-modal">
          <form onSubmit={submitQuote} className="max-h-[90vh] w-full max-w-lg space-y-4 overflow-y-auto rounded-lg border border-white/10 bg-coal p-8">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-ivory">Demande de devis</h3>
              <button type="button" onClick={() => setQuote(null)} aria-label="Fermer">
                <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
              </button>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Type de prestation</label>
              <select
                value={quote.service}
                onChange={(e) => setQuote((q) => ({ ...q, service: e.target.value }))}
                className="input-dark !h-11 w-full"
                data-testid="quote-service-input"
              >
                <option value="">Sélectionnez une prestation…</option>
                {(services || []).map((s) => (
                  <option key={s.id} value={s.name}>{s.name}</option>
                ))}
                <option value="Autre / sur mesure">Autre / sur mesure</option>
              </select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input required value={quote.name} onChange={(e) => setQuote((q) => ({ ...q, name: e.target.value }))} placeholder="Nom et prénom" className="input-dark !h-11" data-testid="quote-name-input" />
              <input value={quote.phone} onChange={(e) => setQuote((q) => ({ ...q, phone: e.target.value }))} placeholder="Téléphone" className="input-dark !h-11" data-testid="quote-phone-input" />
            </div>
            <input required type="email" value={quote.email} onChange={(e) => setQuote((q) => ({ ...q, email: e.target.value }))} placeholder="Email" className="input-dark !h-11" data-testid="quote-email-input" />
            <input value={quote.address} onChange={(e) => setQuote((q) => ({ ...q, address: e.target.value }))} placeholder="Adresse ou commune de la prestation" className="input-dark !h-11" data-testid="quote-address-input" />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Surface (m²)</label>
                <input type="number" min="0" value={quote.surface} onChange={(e) => setQuote((q) => ({ ...q, surface: e.target.value }))} placeholder="ex : 80" className="input-dark !h-11" data-testid="quote-surface-input" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Date souhaitée</label>
                <input type="date" value={quote.preferred_date} onChange={(e) => setQuote((q) => ({ ...q, preferred_date: e.target.value }))} min={new Date().toISOString().split("T")[0]} className="input-dark !h-11 [color-scheme:dark]" data-testid="quote-date-input" />
              </div>
            </div>
            <textarea required rows={4} value={quote.message} onChange={(e) => setQuote((q) => ({ ...q, message: e.target.value }))} placeholder="Décrivez votre besoin (fréquence, particularités, accès…)" className="input-dark !h-auto py-3" data-testid="quote-message-input" />
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                Photos du besoin (optionnel — 4 max)
              </label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={addQuotePhotos}
                className="w-full text-xs text-ivory/60 file:mr-3 file:rounded-md file:border-0 file:bg-copper file:px-4 file:py-2 file:text-xs file:font-semibold file:uppercase file:tracking-wider file:text-ivory"
                data-testid="quote-photos-input"
              />
              {(quote.photos || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="quote-photos-preview">
                  {quote.photos.map((p, i) => (
                    <div key={i} className="relative">
                      <img src={p} alt="" className="h-16 w-16 rounded-md border border-white/10 object-cover" />
                      <button
                        type="button"
                        onClick={() => setQuote((q) => ({ ...q, photos: q.photos.filter((_, j) => j !== i) }))}
                        aria-label="Retirer la photo"
                        className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-ink text-ivory/70 hover:text-red-400"
                        data-testid={`quote-photo-remove-${i}`}
                      >
                        <X className="h-3 w-3" strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button type="submit" disabled={sending} className="btn-primary w-full" data-testid="quote-submit-button">
              {sending ? "Envoi…" : "Envoyer ma demande"}
            </button>
          </form>
        </div>
      )}

      {/* Modal réservation */}
      {booking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="booking-modal">
          <form onSubmit={submitBooking} className="max-h-[90vh] w-full max-w-lg space-y-4 overflow-y-auto rounded-lg border border-white/10 bg-coal p-8">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-ivory">
                {booking.service.name} — {fmt(booking.service.price)}
                {booking.service.unit ? ` ${booking.service.unit}` : ""}
              </h3>
              <button type="button" onClick={() => setBooking(null)} aria-label="Fermer">
                <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
              </button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input value={booking.name} onChange={(e) => setBooking((b) => ({ ...b, name: e.target.value }))} placeholder="Nom et prénom" className="input-dark !h-11" data-testid="booking-name-input" />
              <input type="email" value={booking.email} onChange={(e) => setBooking((b) => ({ ...b, email: e.target.value }))} placeholder="Email" className="input-dark !h-11" data-testid="booking-email-input" />
            </div>
            <input value={booking.phone} onChange={(e) => setBooking((b) => ({ ...b, phone: e.target.value }))} placeholder="Téléphone" className="input-dark !h-11" data-testid="booking-phone-input" />
            <div className="grid gap-4 sm:grid-cols-3">
              <input type="date" value={booking.date} onChange={(e) => setBooking((b) => ({ ...b, date: e.target.value }))} min={new Date().toISOString().split("T")[0]} className="input-dark !h-11 [color-scheme:dark]" data-testid="booking-date-input" />
              <input value={booking.time} onChange={(e) => setBooking((b) => ({ ...b, time: e.target.value }))} placeholder="09h00" className="input-dark !h-11" data-testid="booking-time-input" />
              <input type="number" min="1" max="50" value={booking.quantity} onChange={(e) => setBooking((b) => ({ ...b, quantity: e.target.value }))} className="input-dark !h-11" data-testid="booking-quantity-input" />
            </div>
            <input value={booking.address} onChange={(e) => setBooking((b) => ({ ...b, address: e.target.value }))} placeholder="Adresse de la prestation" className="input-dark !h-11" data-testid="booking-address-input" />
            <textarea rows={2} value={booking.notes} onChange={(e) => setBooking((b) => ({ ...b, notes: e.target.value }))} placeholder="Précisions éventuelles" className="input-dark !h-auto py-3" data-testid="booking-notes-input" />
            <div className="flex items-center gap-2">
              <input
                value={booking.codeInput}
                onChange={(e) => setBooking((b) => ({ ...b, codeInput: e.target.value }))}
                placeholder="Code promo"
                className="input-dark !h-10 flex-1 uppercase !text-xs"
                data-testid="booking-promo-input"
              />
              <button type="button" onClick={applyBookingCode} className="btn-outline !h-10 !px-4 !text-[11px]" data-testid="booking-promo-apply">
                Appliquer
              </button>
            </div>
            {bookingDiscount > 0 && (
              <div className="flex items-center justify-between rounded-md border border-gold/30 px-4 py-2.5 text-sm" data-testid="booking-discount">
                <span className="text-gold">{bookingPromo.name} ({promoLabel(bookingPromo)})</span>
                <span className="text-gold">-{fmt(bookingDiscount)}</span>
              </div>
            )}
            <button type="submit" disabled={sending} className="btn-primary w-full" data-testid="booking-pay-button">
              <CreditCard className="h-4 w-4" strokeWidth={1.5} />
              {sending
                ? "Redirection…"
                : `Payer — ${fmt(bookingTotal - bookingDiscount)}`}
            </button>
            <p className="text-center text-[11px] text-ivory/35">Paiement sécurisé par Stripe.</p>
          </form>
        </div>
      )}

      {/* Avant / Après */}
      {avantApres.length > 0 && (
        <section className="border-t border-white/5 px-6 py-20 md:px-12" data-testid="services-avantapres-section">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <p className="overline mb-6">Nos réalisations</p>
              <h2 className="max-w-2xl text-2xl font-bold tracking-tight text-ivory sm:text-3xl">
                Avant / Après
              </h2>
            </Reveal>
            <div className="mt-12 grid gap-8 md:grid-cols-2">
              {avantApres.map((pair, i) => (
                <Reveal key={pair.avant.pair_key || i} delay={(i % 2) * 0.08}>
                  <div className="grid grid-cols-2 gap-3" data-testid={`services-pair-${i}`}>
                    <div>
                      <PhotoSlot src={pair.avant.photo} alt={pair.avant.caption || "Avant"} ratio="aspect-square" />
                      <p className="mt-2 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-ivory/40">Avant</p>
                    </div>
                    <div>
                      <PhotoSlot src={pair.apres.photo} alt={pair.apres.caption || "Après"} ratio="aspect-square" />
                      <p className="mt-2 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-gold">Après</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Galerie */}
      {galleryPhotos.length > 0 && (
        <section className="border-t border-white/5 px-6 py-20 md:px-12" data-testid="services-gallery-section">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <p className="overline mb-6">En images</p>
              <h2 className="max-w-2xl text-2xl font-bold tracking-tight text-ivory sm:text-3xl">
                Nos interventions
              </h2>
            </Reveal>
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="services-gallery-grid">
              {galleryPhotos.map((g, i) => (
                <Reveal key={g.id} delay={(i % 4) * 0.06}>
                  <div className="flex h-full flex-col rounded-lg border border-white/10 bg-coal/60 p-4" data-testid={`services-gallery-card-${i}`}>
                    <PhotoSlot src={g.photo} alt={g.title || g.caption || "Intervention Maasaï Services"} ratio="aspect-[4/3]" />
                    {(g.title || g.caption) && (
                      <h3 className="mt-4 text-sm font-semibold tracking-tight text-ivory">
                        {g.title || g.caption}
                      </h3>
                    )}
                    {g.description && (
                      <p className="mt-2 flex-1 text-xs leading-relaxed text-ivory/50">{g.description}</p>
                    )}
                    <Link
                      to="/contact?activity=services"
                      className="mt-4 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-gold/80 transition-colors hover:text-gold"
                      data-testid={`services-gallery-contact-${i}`}
                    >
                      Contactez-nous
                    </Link>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <FaqSection scope="services" title="Vos questions sur nos prestations" />

      <CustomSections page="services" />

      {/* Nous recrutons */}
      <section className="border-t border-white/5 px-6 py-24 md:px-12 md:py-28" data-testid="recruit-section">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2 lg:items-start">
          <Reveal>
            <p className="overline mb-6">Nous recrutons</p>
            <h2 className="text-2xl font-bold tracking-tight text-ivory sm:text-3xl">
              Rejoignez l'équipe Maasaï Services
            </h2>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-ivory/55">
              Nous recrutons régulièrement pour nos missions de ménage et d'entretien : missions
              ponctuelles, jobs étudiants et temps partiel. Déposez votre CV, nous revenons vers
              vous dès qu'une mission correspond à votre profil.
            </p>
            <ul className="mt-8 space-y-3">
              {["Missions ponctuelles et flexibles", "Jobs étudiants (soirs, week-ends)", "Temps partiel évolutif"].map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm text-ivory/65">
                  <Check className="h-4 w-4 shrink-0 text-gold" strokeWidth={1.5} />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="recruit-card">
              {appSent ? (
                <div className="py-8 text-center" data-testid="recruit-success">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/40">
                    <Check className="h-6 w-6 text-gold" strokeWidth={1.5} />
                  </span>
                  <h3 className="mt-6 text-lg font-semibold text-ivory">Candidature envoyée !</h3>
                  <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-ivory/55">
                    Merci ! Votre CV a bien été transmis. Nous vous recontacterons dès qu'une
                    mission correspondra à votre profil.
                  </p>
                </div>
              ) : (
                <form onSubmit={submitApplication} className="space-y-4">
                  <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-ivory">
                    <Upload className="h-4 w-4 text-gold" strokeWidth={1.5} />
                    Déposez votre CV
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input
                      value={application.name}
                      onChange={(e) => setApplication((a) => ({ ...a, name: e.target.value }))}
                      placeholder="Nom et prénom"
                      className="input-dark !h-11"
                      data-testid="recruit-name-input"
                    />
                    <input
                      value={application.phone}
                      onChange={(e) => setApplication((a) => ({ ...a, phone: e.target.value }))}
                      placeholder="Téléphone"
                      className="input-dark !h-11"
                      data-testid="recruit-phone-input"
                    />
                  </div>
                  <input
                    type="email"
                    value={application.email}
                    onChange={(e) => setApplication((a) => ({ ...a, email: e.target.value }))}
                    placeholder="Email"
                    className="input-dark !h-11"
                    data-testid="recruit-email-input"
                  />
                  <textarea
                    rows={3}
                    value={application.message}
                    onChange={(e) => setApplication((a) => ({ ...a, message: e.target.value }))}
                    placeholder="Votre message (disponibilités, expérience…)"
                    className="input-dark !h-auto py-3"
                    data-testid="recruit-message-input"
                  />
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                      CV (PDF, DOC ou DOCX — 5 Mo max)
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => setCvFile(e.target.files?.[0] || null)}
                      className="w-full text-xs text-ivory/60 file:mr-3 file:rounded-md file:border-0 file:bg-copper file:px-4 file:py-2 file:text-xs file:font-semibold file:uppercase file:tracking-wider file:text-ivory"
                      data-testid="recruit-cv-input"
                    />
                    {cvFile && (
                      <p className="mt-2 text-xs text-gold" data-testid="recruit-cv-name">{cvFile.name}</p>
                    )}
                  </div>
                  <button type="submit" disabled={appSending} className="btn-primary w-full" data-testid="recruit-submit-button">
                    {appSending ? "Envoi en cours…" : "Envoyer ma candidature"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <UniverseSection current="services" />
    </div>
  );
}

export function ServiceSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("loading");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/services/bookings/payment-status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      attempts += 1;
      if (attempts < 10) {
        timer = setTimeout(poll, 2000);
      } else {
        setState("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="service-success-page">
      <PageHero
        overline="Maasaï Services"
        title={state === "paid" ? "Prestation confirmée" : "Confirmation en cours"}
        testId="services-hero"
      />
      <section className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto max-w-2xl">
          <Reveal>
            <div className="rounded-lg border border-gold/30 bg-coal/60 p-12 text-center">
              {state === "loading" && (
                <p className="text-sm text-ivory/60" data-testid="service-payment-loading">
                  Vérification du paiement…
                </p>
              )}
              {state === "paid" && (
                <>
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/40">
                    <Check className="h-6 w-6 text-gold" strokeWidth={1.5} />
                  </span>
                  <h2 className="mt-8 text-2xl font-semibold tracking-tight text-ivory">Merci !</h2>
                  <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-ivory/55" data-testid="service-payment-confirmed">
                    Votre prestation est confirmée et réglée. Un email de confirmation vient de vous
                    être envoyé.
                  </p>
                </>
              )}
              {state === "pending" && (
                <p className="text-sm text-ivory/60" data-testid="service-payment-pending">
                  Paiement en cours de traitement. Vous recevrez un email de confirmation.
                </p>
              )}
              {state === "error" && (
                <p className="text-sm text-red-400" data-testid="service-payment-error">
                  Impossible de vérifier le paiement. Merci de nous contacter sur WhatsApp.
                </p>
              )}
              <Link to="/services" className="btn-outline mt-10" data-testid="service-success-back">
                Retour aux prestations
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
